<script lang="ts" setup>
import { ref, reactive, watchEffect } from 'vue'
import Modal from '~/components/common/modal.vue'
import { useModal } from '~/composables/common/use-modal'
import { useLogin } from '~/composables/account/use-login'
import type { DropdownItem } from '~/composables/common/interface'
import { gameFilterCategory } from '~/resources/lobby-games/banner'

const { isLogged, showModalLogin } = useLogin()
const props = defineProps({
  optionItems: {
    type: Array as () => DropdownItem[],
    required: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  customClass: {
    type: String,
    default: ''
  },
  modalId: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  isLabelFloating: { type: Boolean, default: false },
  iconTypeImage: {
    type: Boolean,
    default: false
  },
  valueSelected: {
    type: String,
    default: ''
  },
  withButton: {
    type: Object as () => { action: () => void, title: string },
    default: undefined
  }
})
const selectedItem = reactive<DropdownItem>({
  ...(props.placeholder
    ? {}
    : props.optionItems[0] ?? {
        label: 'Vui lòng chọn 1 mục',
        value: '',
        icon: '',
        count: 0
      })
})

watchEffect(() => {
  const selected = props.optionItems.find(item => item.value === props.valueSelected)
  if (selected) {
    Object.assign(selectedItem, selected)
  } else {
    Object.assign(selectedItem, { label: props.placeholder ?? 'Vui lòng chọn 1 mục', value: '', icon: '', count: 0 })
  }
})

const { openModal, closeModal } = useModal()
const showOptions = ref(false)
const emits = defineEmits(['handleItemSelected'])

function toggleDropdown(): void {
  openModal(props.modalId)
  showOptions.value = !showOptions.value
}
const selectItem = (item: DropdownItem): void => {
  showOptions.value = false
  closeModal(props.modalId)
  if (!isLogged.value && item?.label === gameFilterCategory[2]?.label) {
    showModalLogin()
    return
  }
  selectedItem.label = item?.label
  selectedItem.value = item?.value
  selectedItem.icon = item?.icon
  selectedItem.count = item?.count
  selectedItem.image = item?.image
  selectedItem.isDisabled = item?.isDisabled
  emits('handleItemSelected', item)
}
const handleClose = (): void => {
  closeModal(props.modalId)
}
</script>
<template>
  <div class="filter-wrapper-dropdown">
    <div :class="['label', { floating: isLabelFloating }]">
      <span>{{ label }}</span>
      <div v-if="isLabelFloating" class="bg-wrapper"></div>
    </div>
    <div class="filter-dropdown" :class="[{ active: showOptions }, customClass]">
      <div class="filter-dropdown__header" @click="toggleDropdown()">
        <img
          v-if="selectedItem.image"
          :src="selectedItem.image"
          :class="['filter-dropdown__icon-image', selectedItem?.value]"
          :alt="selectedItem.label"
          width="20"
          height="20"
        />
        <img
          v-else-if="iconTypeImage && selectedItem.icon"
          :src="selectedItem.icon"
          class="filter-dropdown__icon-image"
          :alt="selectedItem.label"
          width="24"
          height="24"
        />
        <span
          v-if="selectedItem?.icon && !iconTypeImage && !selectedItem.image"
          class="filter-dropdown__icon"
          :class="selectedItem?.icon"
        ></span>
        <span :class="['filter-dropdown__name text-truncate', { 'not-selected': placeholder && !selectedItem?.value }]">
          {{ selectedItem?.label ?? placeholder }}
        </span>
        <span v-if="selectedItem?.isDisabled" class="bao-tri-label">Bảo trì</span>
        <span class="icon-arrow-down"></span>
      </div>
    </div>
    <Modal
      :modalId="modalId"
      :isShowIconClose="false"
      modalWrapperClasses="modal-select"
      modalHeaderClasses="modal-select__header"
    >
      <div class="content-select">
        <div class="content-select__title">
          {{ label }}
          <div class="modal-icon-close" @click="handleClose">
            <span class="icon-close"></span>
          </div>
        </div>
        <ul class="list-select">
          <li
            v-for="item in optionItems"
            :key="item.value"
            :class="[
              'list-select__item d-flex align-items-center justify-content-between',
              {
                active: item.label === selectedItem?.label,
                'list-select__item--under-maintenance': item.isDisabled
              },
              item.value
            ]"
            @click="selectItem(item)"
          >
            <img
              v-if="item.image"
              :src="item.image"
              class="list-select__icon-image"
              :alt="item.label"
              width="20"
              height="20"
            />
            <img
              v-else-if="iconTypeImage"
              :src="item.value === selectedItem?.value ? item.icon : item.iconGray"
              class="list-select__icon-image"
              :alt="item.label"
              width="24"
              height="24"
            />
            <span v-if="item.icon && !iconTypeImage && !item.image" class="list-select__icon" :class="item.icon"></span>
            <span :class="['list-select__name', item.key, item.textClassStatus]">{{ item.label }}</span>
            <span class="icon">
              <img v-if="item.value === selectedItem?.value" src="/assets/images/icons/red-arrow.svg" alt="icon" />
            </span>
          </li>
        </ul>
        <div v-if="withButton" class="content-select__button">
          <button class="button main main--primary-outline w-100" @click="withButton.action">
            {{ withButton.title }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
@import '~scssAsset/mobile/common/filter-dropdown-v2.scss';
</style>
